import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Wallet } from 'lucide-react';
import EligibleCashbackIcon from '../images/EligibleCashback.svg';
import RedeemableCashbackIcon from '../images/RedeemableCashback.svg';
import LifetimeCashbackIcon from '../images/LifetimeCashback.svg';
import { getSubscriberProfile, getWalletSnapshot, getHospitalBookingDetail, getApplicationHospitalBookings } from '../services/subscriberWalletService';
import { formatDateTimeIST } from '../utils/dateFormatter';
import { HOSPITAL_PAYMENT_LIST_PATH, HOSPITAL_PAYMENT_LIST_URL_KEY, resolveListBackTarget } from '../utils/subscriberHelpers';

const currencyRounded = (value) => {
  const num = Number(value);
  if (isNaN(num)) return '₹0';
  const truncated = Math.trunc(num * 100) / 100;
  const fixed = truncated.toFixed(2);
  const [wholePart, decimalPart] = fixed.split('.');
  const formattedWhole = Number(wholePart).toLocaleString('en-IN');
  if (decimalPart === '00') return `₹${formattedWhole}`;
  return `₹${formattedWhole}.${decimalPart}`;
};

const getStatusBadgeStyle = (status) => {
  const statusLower = String(status || '').toLowerCase().trim();
  const statusColorMap = {
    'success':     { text: '#16A34A', bg: 'rgba(22, 163, 74, 0.1)' },
    'approved':    { text: '#16A34A', bg: 'rgba(22, 163, 74, 0.1)' },
    'failed':      { text: '#DC2626', bg: 'rgba(220, 38, 38, 0.1)' },
    'cancelled':   { text: '#DC2626', bg: 'rgba(220, 38, 38, 0.1)' },
    'pending':     { text: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)' },
    'in_progress': { text: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)' },
    'refunded':    { text: '#0EA5E9', bg: 'rgba(14, 165, 233, 0.1)' },
    'initiated':   { text: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)' },
    'complete':    { text: '#15803D', bg: 'rgba(21, 128, 61, 0.1)' },
    'completed':   { text: '#15803D', bg: 'rgba(21, 128, 61, 0.1)' },
  };
  const colorConfig = statusColorMap[statusLower] || { text: '#6B7280', bg: 'rgba(107, 114, 128, 0.1)' };
  return {
    color: colorConfig.text,
    backgroundColor: colorConfig.bg,
    borderColor: colorConfig.text,
    minWidth: '90px',
    justifyContent: 'center',
  };
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  if (typeof dateString === 'object' && dateString !== null && !(dateString instanceof Date)) {
    if (dateString.start_date && dateString.end_date) {
      return `${formatDate(String(dateString.start_date))} - ${formatDate(String(dateString.end_date))}`;
    }
    return 'N/A';
  }
  if (typeof dateString !== 'string') return 'N/A';
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (dateString.includes(' to ') || dateString.includes(' - ')) {
      const sep = dateString.includes(' to ') ? ' to ' : ' - ';
      const [s, e] = dateString.split(sep);
      return `${formatDate(s.trim())} - ${formatDate(e.trim())}`;
    }
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      const [day, monthNum, year] = dateString.split('/');
      const d = new Date(`${year}-${monthNum.padStart(2, '0')}-${day.padStart(2, '0')}`);
      if (!isNaN(d.getTime())) return `${day.padStart(2, '0')}-${monthNames[d.getMonth()]}-${year}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return `${String(date.getDate()).padStart(2, '0')}-${monthNames[date.getMonth()]}-${date.getFullYear()}`;
  } catch {
    return dateString;
  }
};

const maskMobile = (value) => {
  if (!value) return 'N/A';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length <= 4) return `******${digits}`;
  return `******${digits.slice(-4)}`;
};

const HospitalBookingProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const bookingId = searchParams.get('booking_id');

  const [booking, setBooking] = useState(location.state?.booking || null);
  const bookingFromState = useRef(!!location.state?.booking);

  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPhone, setShowPhone] = useState(false);
  const isLoadingRef = useRef(false);

  const [allBookings, setAllBookings] = useState([]);
  const [allBookingsLoading, setAllBookingsLoading] = useState(true);
  const [allBookingsError, setAllBookingsError] = useState(null);
  const allBookingsLoadingRef = useRef(false);

  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance',
      'wallet_ops': 'Ops',
      'wallet_support': 'Support',
    };
    return roleMapping[authRole] || null;
  })();

  const isFinance = currentRole === 'Finance';
  const isSupport = currentRole === 'Support';

  const maskedPhone = React.useMemo(() => {
    if (!profile) return 'N/A';
    const phone = profile?.personal?.mobile || profile?.phoneFull || '';
    if (!phone || phone.trim() === '') return 'N/A';
    return maskMobile(phone);
  }, [profile]);

  useEffect(() => {
    if (isLoadingRef.current || !id || !currentRole) return;
    isLoadingRef.current = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const requests = [
          getSubscriberProfile(id, currentRole).catch(err => ({ error: 'Profile API failed', details: err.message })),
          getWalletSnapshot(id, currentRole).catch(err => ({ error: 'Wallet API failed', details: err.message })),
          // Fetch booking from API only when not passed via navigation state (e.g. page refresh)
          (!bookingFromState.current && bookingId)
            ? getHospitalBookingDetail(currentRole, bookingId).catch(() => null)
            : Promise.resolve(null),
        ];

        const results = await Promise.allSettled(requests);
        const [profileData, walletData, bookingData] = results;

        if (profileData.status === 'fulfilled' && profileData.value) {
          const res = profileData.value;
          const p = res.msg ?? res.data ?? (typeof res === 'object' ? res : null);
          if (p) setProfile(p);
        }

        if (walletData.status === 'fulfilled' && walletData.value) {
          const res = walletData.value;
          if (res.wallet_snapshot) setWallet({ wallet_snapshot: res.wallet_snapshot });
          else if (res.msg?.wallet_snapshot) setWallet({ wallet_snapshot: res.msg.wallet_snapshot });
          else if (res.data?.wallet_snapshot) setWallet({ wallet_snapshot: res.data.wallet_snapshot });
          else if (res.error) setWallet({ error: res.error });
        }

        if (bookingData && bookingData.status === 'fulfilled' && bookingData.value) {
          const res = bookingData.value;
          const b = res.msg ?? res.data ?? null;
          if (b) setBooking(b);
        }
      } catch {
        setError('Failed to load subscriber data');
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    };

    loadData();
  }, [id, currentRole, bookingId]);

  useEffect(() => {
    if (!id || !currentRole || allBookingsLoadingRef.current) return;
    allBookingsLoadingRef.current = true;
    const load = async () => {
      try {
        setAllBookingsLoading(true);
        setAllBookingsError(null);
        const res = await getApplicationHospitalBookings(currentRole, id, { items: 100 });
        const src = res?.msg || res?.data || res;
        const items = src?.items || (Array.isArray(src) ? src : []);
        setAllBookings(items);
      } catch {
        setAllBookingsError('Failed to load bookings');
      } finally {
        setAllBookingsLoading(false);
        allBookingsLoadingRef.current = false;
      }
    };
    load();
  }, [id, currentRole]);

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
          <button onClick={() => navigate('/subscriber-wallet')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Go to Home</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Retry</button>
        </div>
      </div>
    );
  }

  const bookingStatus = booking?.booking_status || booking?.status;
  const bookingAmount = booking?.price ?? booking?.amount ?? 0;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">

        {/* Navigation */}
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/subscriber-wallet')}
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
          <button
            onClick={() => navigate(resolveListBackTarget(location.state, HOSPITAL_PAYMENT_LIST_PATH, HOSPITAL_PAYMENT_LIST_URL_KEY))}
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Hospital Payments
          </button>
        </div>

        <div className="mt-2 mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Hospital Booking Profile</h1>
        </div>

        {/* Subscriber Info */}
        <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-6 shadow-sm">
          <div className="space-y-6">
            <div>
              <div className="text-xl font-bold text-gray-900">
                {profile?.personal?.full_name || profile?.name || booking?.subscriber_name || 'Subscriber'}
              </div>
              <div className="text-sm text-gray-600 flex items-center space-x-2 mt-1">
                <span>
                  Mobile No:{' '}
                  {(isFinance || isSupport)
                    ? (profile?.personal?.mobile || profile?.phoneFull || 'N/A')
                    : (showPhone ? (profile?.personal?.mobile || profile?.phoneFull || 'N/A') : maskedPhone)}
                </span>
                {!(isFinance || isSupport) && (
                  <button
                    onClick={() => setShowPhone(v => !v)}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label={showPhone ? 'Hide phone' : 'Show phone'}
                  >
                    {showPhone ? <FaEyeSlash /> : <FaEye />}
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">REF CODE</div>
                  <div className="text-sm font-medium text-gray-900">{profile?.subscription || booking?.ref_code || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Validity Period</div>
                  <div className="text-sm font-medium text-gray-900">{formatDate(profile?.valid_period)}</div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Plan</div>
                  <div className="text-sm font-medium text-gray-900">{profile?.plan || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">KYC Status</div>
                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-md bg-green-500 text-white w-fit">
                    {profile?.kyc || 'N/A'}
                  </span>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-md w-fit ${
                    profile?.status === 'Active' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
                  }`}>
                    {profile?.status || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wallet Snapshot */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Wallet Snapshot</h2>
            {wallet?.wallet_snapshot?.ref_code && (
              <div className="text-sm text-gray-500">
                Ref: <span className="font-mono text-gray-700">{wallet.wallet_snapshot.ref_code}</span>
              </div>
            )}
          </div>

          {!wallet ? (
            <div className="bg-white border border-gray-200 rounded-md p-6 text-center">
              <div className="text-gray-500 mb-2">Loading wallet data...</div>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : wallet.error ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-6 text-center">
              <div className="text-red-600">Failed to load wallet data</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'WB (Wallet Balance)', value: currencyRounded(wallet?.wallet_snapshot?.balance || 0), icon: <Wallet className="w-4 h-4 text-blue-600" /> },
                { label: 'EC (Eligible Cashback)', value: currencyRounded(wallet?.wallet_snapshot?.eligible_cashback || 0), icon: <img src={EligibleCashbackIcon} alt="Eligible Cashback" className="w-4 h-4" /> },
                { label: 'RC (Redeemable Cashback)', value: currencyRounded(wallet?.wallet_snapshot?.redeemable_cashback || 0), icon: <img src={RedeemableCashbackIcon} alt="Redeemable Cashback" className="w-4 h-4" /> },
                { label: 'LC (Lifetime Cashback)', value: currencyRounded(wallet?.wallet_snapshot?.lifetime_cashback || 0), icon: <img src={LifetimeCashbackIcon} alt="Lifetime Cashback" className="w-4 h-4" /> },
              ].map((c) => (
                <div key={c.label} className="bg-white border border-gray-200 rounded-md p-4 hover:shadow-lg transition-shadow duration-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-blue-50 rounded-md flex items-center justify-center">{c.icon}</div>
                    <div className="text-sm text-gray-600">{c.label}</div>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-2">{c.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Hospital Booking Details */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Hospital Booking Details</h2>
          <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-6 shadow-sm">
            {!booking ? (
              <div className="text-center text-gray-500 py-8">
                No booking data available. Please navigate here from the Hospital Payments list.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Date & Time</div>
                  <div className="text-sm font-medium text-gray-900">{formatDateTimeIST(booking.date_time) || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Hospital</div>
                  <div className="text-sm font-medium text-gray-900">{booking.hospital_name || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Bill Amount</div>
                  <div className="text-sm font-semibold text-gray-900">
                    ₹{bookingAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">EC (Eligible Cashback)</div>
                  <div className="text-sm font-semibold text-gray-900">{currencyRounded(booking.eligible_cashback || 0)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">RC (Redeemable Cashback)</div>
                  <div className="text-sm font-semibold text-gray-900">{currencyRounded(booking.redeemable_cashback || 0)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">WB (Wallet Balance)</div>
                  <div className="text-sm font-semibold text-gray-900">{currencyRounded(booking.wallet_balance || 0)}</div>
                </div>
                {booking.reason && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Reason</div>
                    <div className="text-sm font-medium text-gray-900">{booking.reason}</div>
                  </div>
                )}
                {booking.treatment_details && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Treatment Details</div>
                    <div className="text-sm font-medium text-gray-900">{booking.treatment_details}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <span
                    className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border"
                    style={getStatusBadgeStyle(bookingStatus)}
                  >
                    {bookingStatus || 'N/A'}
                  </span>
                </div>
                {(booking.booking_id || bookingId) && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Booking ID</div>
                    <div className="text-sm font-medium text-gray-900 font-mono">{booking.booking_id || bookingId}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* All Hospital Bookings for this Application */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">All Hospital Bookings</h2>
          <div className="bg-white border border-gray-200 rounded-md shadow-sm">
            {allBookingsLoading ? (
              <div className="flex justify-center items-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : allBookingsError ? (
              <div className="px-6 py-8 text-center text-red-600">{allBookingsError}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hospital</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cashback</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allBookings.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">No hospital bookings found</td>
                      </tr>
                    ) : (
                      allBookings.map((b) => {
                        const bId = b.booking_id || b.id;
                        const isHighlighted = bookingId && String(bId) === String(bookingId);
                        return (
                          <tr key={bId} className={isHighlighted ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-gray-50'}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateTimeIST(b.date_time)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{b.hospital_name || 'N/A'}</td>
                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{b.reason || b.treatment_reason || 'N/A'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{currencyRounded(b.price || b.amount || 0)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{currencyRounded(b.cashback || b.cashback_amount || 0)}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border"
                                style={getStatusBadgeStyle(b.booking_status || b.status)}
                              >
                                {b.booking_status || b.status || 'N/A'}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default HospitalBookingProfile;
